/**
 * Constants and configuration for authentication flow
 */

import type { AuthPhase } from './types';

// Storage keys
export const STORAGE_KEYS = {
  PROFILE_PHASE: 'profile_auth_phase',
  PROFILE_TIMESTAMP: 'profile_auth_timestamp',
  ENHANCED_AUTH_STATE: 'enhanced_auth_state',
  AUTH_STATE_TIMESTAMP: 'auth_state_timestamp',
  STATE_VERSION: 'auth_state_version'
} as const;

// Session expiration time (24 hours in milliseconds)
export const SESSION_EXPIRATION_TIME = 24 * 60 * 60 * 1000;

// UI timing constants (in milliseconds)
export const UI_TIMEOUTS = {
  SUCCESS_DISPLAY: 1500, // Time to show success state before redirect
  NAVIGATION_DELAY: 100, // Small delay for navigation to ensure UI updates
  QUEUE_PROCESS_DELAY: 0 // Process queue immediately but async
} as const;

// Queue limits
export const QUEUE_LIMITS = {
  MAX_NAVIGATION_QUEUE_SIZE: 10,
  MAX_RETRY_ATTEMPTS: 3
} as const;

// Non-recoverable phases (security-sensitive, transient, or decision points)
// Users cannot resume from these phases after closing the dialog
// IMPORTANT: One-way gates MUST be non-recoverable to maintain consistency
export const NON_RECOVERABLE_PHASES: AuthPhase[] = [
  // Initial/Terminal states
  'idle', // No state to recover
  'complete', // Flow already finished
  'error', // Error state should be re-triggered

  // Authentication phases (security-sensitive)
  'contact', // Initial entry point - start fresh each time
  'otp', // Verification codes expire
  'password', // Don't store password entry state
  'passwordSignup', // Don't store password creation state
  'setPassword', // Don't store new password state
  'identityLinkingOtp', // Verification codes expire

  // Decision phases (one-time decisions that affect account state)
  'accountDiscovery', // Account merge decision - can't re-decide
  'identityConflict' // Critical one-way gate - must not be re-enterable

  // Note: alternateContact IS recoverable unless discovery/conflict is in progress
  // Note: generalProfile and role profiles ARE recoverable (pure data entry)
];

// Phases that cannot have back navigation
export const NO_BACK_PHASES: AuthPhase[] = ['idle', 'contact', 'otp', 'complete', 'error'];

// Role profile phases
export const ROLE_PHASES: AuthPhase[] = [
  'teacherProfile',
  'studentProfile',
  'parentProfile',
  'employerProfile',
  'schoolProfile',
  'organizerProfile'
];

// Available user types for selection
export const AVAILABLE_USER_TYPES = [
  { value: 'teacher', label: 'Teacher', icon: 'mdi-school' },
  { value: 'student', label: 'Student', icon: 'mdi-account-school' },
  { value: 'parent', label: 'Parent', icon: 'mdi-account-child' },
  { value: 'employer', label: 'Employer', icon: 'mdi-domain' },
  { value: 'school', label: 'School Admin', icon: 'mdi-school-outline' },
  { value: 'organizer', label: 'Organizer', icon: 'mdi-account-group' }
];

// Phase flow configuration
export const PHASE_FLOW = {
  setPassword: 'alternateContact',
  alternateContact: {
    withDiscovery: 'accountDiscovery',
    default: 'generalProfile'
  },
  accountDiscovery: 'generalProfile',
  generalProfile: {
    withIdentityLinking: 'identityLinkingOtp',
    withRoles: (firstRole: string) => `${firstRole.toLowerCase()}Profile`,
    complete: 'complete'
  },
  identityLinkingOtp: {
    withRoles: (firstRole: string) => `${firstRole.toLowerCase()}Profile`,
    complete: 'complete'
  },
  identityConflict: {
    withRoles: (firstRole: string) => `${firstRole.toLowerCase()}Profile`,
    complete: 'complete'
  }
};

// Error messages
export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  INVALID_OTP: 'Invalid verification code. Please check and try again.',
  EXPIRED_OTP: 'Verification code has expired. Please request a new one.',
  ACCOUNT_EXISTS: 'An account with this email/phone already exists.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  PROFILE_INCOMPLETE: 'Please complete all required fields.',
  IDENTITY_CONFLICT: 'This account is already linked to another user.',
  MERGE_FAILED: 'Failed to merge accounts. Please try again.',
  INVALID_CREDENTIALS: 'Invalid email/phone or password.',
  AUTH_FAILED_NO_USER: 'Authentication failed - no user found',
  NAVIGATION_QUEUE_OVERFLOW: 'Too many navigation requests. Please wait.',
  PROFILE_SAVE_FAILED: 'Failed to save profile data. Please try again.',
  SESSION_RECOVERY_FAILED: 'Failed to recover session. Please log in again.'
};

// Success messages (kept concise for compact UI)
export const SUCCESS_MESSAGES = {
  OTP_SENT: 'Code sent!',
  OTP_RESENT: 'Code resent!',
  ACCOUNT_CREATED: 'Account created!',
  PROFILE_SAVED: 'Profile saved!',
  IDENTITY_LINKED: 'Linked!',
  ACCOUNTS_MERGED: 'Accounts merged!',
  PASSWORD_SET: 'Password set!',
  PASSWORD_RESET_SENT: 'Reset link sent if account exists'
};

// Helper to determine message type for UI styling
export type AlertType = 'success' | 'error' | 'info' | 'warning';

const SUCCESS_PATTERNS = Object.values(SUCCESS_MESSAGES);

export const getMessageAlertType = (message: string | null | undefined): AlertType => {
  if (!message) return 'info';

  // Check if message matches any success pattern
  if (SUCCESS_PATTERNS.some(pattern => message.includes(pattern) || pattern.includes(message))) {
    return 'success';
  }

  // Default to error for unrecognized messages
  return 'error';
};

export const getMessageIcon = (message: string | null | undefined): string => {
  const type = getMessageAlertType(message);
  switch (type) {
    case 'success':
      return 'mdi-check-circle';
    case 'error':
      return 'mdi-alert-circle';
    case 'warning':
      return 'mdi-alert';
    case 'info':
    default:
      return 'mdi-information';
  }
};
